import React from 'react';
import { Box, Typography, Paper, Chip, Alert, Stack } from '@mui/material';
import {
  CheckCircle,
  Warning,
  Info,
  TrendingUp,
  Groups,
} from '@mui/icons-material';
import { TeamDiscoveryResult, TeamBalancer } from '../types';
import { QUADRANT_LABELS } from './TeamMap';

const TEAM_TYPE_DESCRIPTIONS: Record<string, { description: string; strengths: string[]; cautions: string[] }> = {
  exploring: {
    description: 'Daring, risk-tolerant, and imaginative. Your team shares ideas freely and informally, with innovation as the goal.',
    strengths: ['Creative problem solving', 'Open communication', 'Willingness to experiment', 'Constructive conflict'],
    cautions: ['May overlook details', 'Can lack follow-through', 'May resist structure'],
  },
  pathfinding: {
    description: 'Fast-paced, goal-oriented, and competitive. Conflict is frequent but seen as valuable for challenging ideas.',
    strengths: ['High energy', 'Drives innovation and results', 'Strong individual contributors', 'Challenges status quo'],
    cautions: ['Poor coordination', 'May not share resources', 'Competitive internal dynamics'],
  },
  producing: {
    description: 'Competitive and intense. Task-oriented with eyes on the prize, cooperation happens when mutually beneficial.',
    strengths: ['Results-driven', 'Strong individual performance', 'Accountable', 'Goal-focused'],
    cautions: ['Internal politics', 'Low cooperation', 'Win-lose mentality'],
  },
  executing: {
    description: 'Disciplined, conscientious, and professional. High performers who work in a controlled, fact-based manner.',
    strengths: ['Efficient', 'Clear accountability', 'Professional standards', 'Defined roles'],
    cautions: ['May lack creativity', 'Low emotional connection', 'Rigid processes'],
  },
  stabilizing: {
    description: 'Structured, task-focused, organized, and practical. By-the-book with clear expectations.',
    strengths: ['Consistent output', 'Clear processes', 'Reliable', 'Risk-averse'],
    cautions: ['Slow to address conflict', 'Resistant to change', 'Information silos'],
  },
  anchoring: {
    description: 'Process-oriented, steady, and eager to help one another. Deliberate and careful in planning.',
    strengths: ['Collaborative', 'Flexible to customer needs', 'Strong processes', 'Supportive culture'],
    cautions: ['Internally focused', 'May resist external change', 'Over-reliance on procedures'],
  },
  cultivating: {
    description: 'Cooperative, supportive, and loyal. Friendly team that respects consensus and builds trust over time.',
    strengths: ['Strong relationships', 'Psychological safety', 'Group decision making', 'Loyalty'],
    cautions: ['Avoids conflict', 'Groupthink risk', 'May not challenge ideas enough'],
  },
  bolstering: {
    description: 'Social, fun, and full of energy. Supportive of each other with a focus on brainstorming and innovation.',
    strengths: ['High morale', 'Creative brainstorming', 'Strong support network', 'Good communication'],
    cautions: ['May avoid hard decisions', 'Can lack focus', 'Conflict avoidance'],
  },
  adapting: {
    description: 'Flexible and well-equipped for new or changing situations. Self-organizing with balanced perspectives.',
    strengths: ['Versatile', 'Quick to adapt', 'Balanced viewpoints', 'Diplomatic'],
    cautions: ['May lack identity', 'Quick resolution over deep exploration', 'Jack-of-all-trades risk'],
  },
};

interface TeamTypeCardProps {
  result: TeamDiscoveryResult;
}

const TeamTypeCard: React.FC<TeamTypeCardProps> = ({ result }) => {
  const toTitleCase = (value: string) => value
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
    .join(' ');
  const displayTeamTypeName = (() => {
    const rawName = String(result.team_type_info?.name || result.team_type || 'Adapting').trim();
    return toTitleCase(rawName);
  })();

  const toSafePercent = (value: unknown) => {
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, Math.min(100, parsed));
  };

  const hasAlignmentScore = result.alignment_score != null && Number.isFinite(Number(result.alignment_score));
  const alignmentPercent = hasAlignmentScore ? toSafePercent(result.alignment_score) : 0;
  const typeInfo = TEAM_TYPE_DESCRIPTIONS[result.team_type] || TEAM_TYPE_DESCRIPTIONS.adapting;
  const alignmentColor = hasAlignmentScore
    ? alignmentPercent >= 70 ? 'success' : alignmentPercent >= 40 ? 'warning' : 'error'
    : 'info';

  return (
    <Stack spacing={2}>
      {/* Team Type */}
      <Paper sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Groups color="primary" />
          <Typography variant="h6">
            {displayTeamTypeName} Team
          </Typography>
          <Chip label={result.team_type_info.tagline} size="small" variant="outlined" />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {typeInfo.description}
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" color="success.main" gutterBottom>Strengths</Typography>
            {typeInfo.strengths.map((s, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
                <Typography variant="body2">{s}</Typography>
              </Box>
            ))}
          </Box>
          <Box>
            <Typography variant="subtitle2" color="warning.main" gutterBottom>Caution Areas</Typography>
            {typeInfo.cautions.map((c, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Warning sx={{ fontSize: 14, color: 'warning.main' }} />
                <Typography variant="body2">{c}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Strategy Alignment */}
      {result.strategy_type && result.strategy_type_info && (
        <Paper sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <TrendingUp color="primary" />
            <Typography variant="h6">Strategy Alignment</Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Chip
              label={`${displayTeamTypeName} Team`}
              color="primary"
              variant="outlined"
            />
            <Typography variant="body2" color="text.secondary">&rarr;</Typography>
            <Chip
              label={`${result.strategy_type_info.name} Strategy`}
              color="secondary"
              variant="outlined"
            />
            <Chip
              label={hasAlignmentScore ? `${Math.round(alignmentPercent)}% aligned` : 'Alignment pending'}
              color={alignmentColor as any}
              size="small"
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {result.strategy_type_info.description}
          </Typography>

          {/* Alignment bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption">0%</Typography>
            <Box sx={{ flex: 1, bgcolor: 'grey.200', borderRadius: 1, height: 10 }}>
              <Box sx={{
                width: `${Math.round(alignmentPercent)}%`,
                bgcolor: alignmentColor === 'success' ? 'success.main' : alignmentColor === 'warning' ? 'warning.main' : 'error.main',
                borderRadius: 1,
                height: '100%',
                transition: 'width 0.5s ease',
              }} />
            </Box>
            <Typography variant="caption">100%</Typography>
          </Box>
        </Paper>
      )}

      {/* Recommendations */}
      {result.recommendations.length > 0 && (
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="h6" gutterBottom>Recommendations</Typography>
          <Stack spacing={1}>
            {result.recommendations.map((rec, i) => (
              <Alert key={i} severity="info" icon={<Info fontSize="small" />} sx={{ py: 0.5 }}>
                <Typography variant="body2">{rec}</Typography>
              </Alert>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Team Balancers */}
      {result.team_balancers.length > 0 && (
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="h6" gutterBottom>Team Balancers</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            These members bring perspectives outside the team's dominant style, helping counterbalance groupthink.
          </Typography>
          <Stack spacing={1}>
            {result.team_balancers.map((b: TeamBalancer, i: number) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={`${b.first_name} ${b.last_name}`}
                  size="small"
                  sx={{ bgcolor: QUADRANT_LABELS[b.primary_quadrant]?.color, color: '#fff' }}
                />
                <Typography variant="body2" color="text.secondary">{b.value}</Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

export default TeamTypeCard;
